/** The option chain at one minute: calls left, strikes down the middle,
 * puts right — the layout every chain on every terminal uses.
 *
 * In-the-money cells are tinted, which is what makes the ATM findable at a
 * glance without reading a single number: calls are ITM BELOW the ATM and
 * puts ITM ABOVE it, so the shading forms two blocks meeting at the money.
 *
 * A missing side renders as an em dash rather than a zero. The capture
 * walks CE and PE together but a tail strike can lose one to a failed
 * fetch, and a zero premium is a price — the absence has to look different
 * from a cheap option.
 *
 * The table scrolls inside its own box so the basket beside it stays put,
 * and it CENTRES ITSELF ON THE ATM — a full chain runs to 40+ strikes, and
 * a fixed-height box scrolled to the top would open on the deep-ITM tail
 * nobody trades, hiding the only rows that matter.
 */

import { useEffect, useRef, type ReactElement } from "react";
import { cn } from "@/lib/cn";
import type { SimChainRow, SimLeg, SimQuote } from "@/lib/simulatorApi";

function price(quote: SimQuote | null): string {
  return quote === null ? "—" : quote.ltp.toFixed(2);
}

/** The IST minute a quote printed, as "HH:MM" - the same shape as `at`. */
function printedAt(quote: SimQuote): string | null {
  if (!quote.as_of) return null;
  return new Date(quote.as_of).toLocaleTimeString("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** True when the quote is older than the clock. The chain is served AS OF
 * the minute - every contract that has traded by then, at its latest print
 * - so a thin contract keeps its last price instead of vanishing. That price
 * must not pass for a live one, hence the dimming and the minute tag. */
function stale(quote: SimQuote | null, at: string): boolean {
  if (quote === null) return false;
  const minute = printedAt(quote);
  return minute !== null && minute !== at;
}

function delta(quote: SimQuote | null): string {
  return quote?.delta == null ? "" : quote.delta.toFixed(2);
}

/** Lakhs, because raw OI on an index chain runs to eight digits and the
 * column is read for its SHAPE — where the wall is — not its exact value.
 * Blank rather than 0 when the session predates OI capture. */
function openInterest(quote: SimQuote | null): string {
  if (quote?.oi == null) return "";
  return `${(quote.oi / 100_000).toFixed(1)}L`;
}

interface RowProps {
  row: SimChainRow;
  atm: number | null;
  onTrade: (leg: SimLeg) => void;
  onRemove: (strike: number, side: "CE" | "PE") => void;
  lots: Map<string, number>;
  at: string;
  /** Attached to the ATM row only, so the scroll box can centre on it. */
  atmRef?: React.Ref<HTMLTableRowElement>;
}

/** One direction's button, with the lot badge that makes a second click
 * legible as "now I hold two" rather than "did that register?". */
function SideButton({
  direction,
  strike,
  side,
  held,
  onClick,
}: Readonly<{
  direction: "BUY" | "SELL";
  strike: number;
  side: "CE" | "PE";
  held: number;
  onClick: () => void;
}>): ReactElement {
  const lotWord = held === 1 ? "lot" : "lots";
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label={`${direction} ${String(strike)} ${side}`}
        className={cn(
          "rounded px-1.5 py-0.5 text-[10px] font-semibold",
          direction === "BUY"
            ? "bg-success-bg text-success hover:opacity-80"
            : "bg-danger-bg text-danger hover:opacity-80",
        )}
        onClick={onClick}
      >
        {direction === "BUY" ? "B" : "S"}
      </button>
      {held > 0 && (
        <span
          aria-label={`${String(held)} ${lotWord} ${direction} ${String(strike)} ${side}`}
          className="pointer-events-none absolute -right-1 -top-1.5 rounded-full bg-accent px-1 text-[9px] font-semibold leading-tight text-on-accent"
        >
          {held}
        </span>
      )}
    </span>
  );
}

/** Buy, sell and remove for one side of one strike.
 *
 * The entry price is taken from the quote SHOWN, so the basket records
 * what you could see when you clicked — not what the server thinks the
 * price is by the time the request lands. A leg with no premium cannot be
 * traded at all, which is why the buttons vanish rather than sending a
 * zero.
 *
 * ⭐ THE LOT COUNT LIVES ON THE BUTTON THAT MADE IT. Reading your position
 * off the basket panel means looking away from the ladder you are working
 * down, and the badge is the only thing that makes a second click legible
 * as "now I hold two" rather than "did that register?".
 *
 * REMOVE appears only once there is something to remove, and clears the
 * whole holding on that contract — see `removeContract` for why it cannot
 * sensibly mean anything narrower from here.
 */
function TradeButtons({
  quote,
  strike,
  side,
  onTrade,
  onRemove,
  lots,
  at,
}: Readonly<{
  quote: SimQuote | null;
  strike: number;
  side: "CE" | "PE";
  onTrade: (leg: SimLeg) => void;
  onRemove: (strike: number, side: "CE" | "PE") => void;
  lots: Map<string, number>;
  at: string;
}>): ReactElement | null {
  if (quote === null) return null;
  const held = (d: "BUY" | "SELL"): number => lots.get(`${String(strike)}-${side}-${d}`) ?? 0;
  const any = held("BUY") + held("SELL") > 0;
  return (
    <span className="inline-flex items-center gap-0.5">
      {(["BUY", "SELL"] as const).map((d) => (
        <SideButton
          key={d}
          direction={d}
          strike={strike}
          side={side}
          held={held(d)}
          onClick={() => {
            // The minute rides along so the MTM curve knows when the
            // position started, and earns nothing before it.
            onTrade({ strike, side, direction: d, lots: 1, entry: quote.ltp, entry_at: at });
          }}
        />
      ))}
      {any && (
        <button
          type="button"
          // Distinct from the basket panel's per-leg "Remove 24100 CE":
          // this one clears the WHOLE holding on the contract, and two
          // controls with the same name and different reach is how a
          // reader ends up deleting more than they meant to.
          aria-label={`Remove all ${String(strike)} ${side}`}
          title="Remove this contract from the basket"
          className="rounded px-1 py-0.5 text-[10px] font-semibold text-ink-muted hover:bg-surface-2 hover:text-danger"
          onClick={() => {
            onRemove(strike, side);
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}

/** The price cell. A quote older than the clock is dimmed and carries the
 * minute it printed, so a carried-forward price never passes for a live one. */
function PriceCell({
  quote,
  at,
  tint,
  right,
}: Readonly<{
  quote: SimQuote | null;
  at: string;
  tint: string | undefined;
  right: boolean;
}>): ReactElement {
  const old = stale(quote, at);
  const minute = old && quote ? printedAt(quote) : null;
  return (
    <td
      className={cn(
        "px-3 py-1.5 font-mono tabular-nums",
        tint,
        right && "text-right",
        old && "text-ink-muted",
      )}
      title={minute ? `last traded ${minute}` : undefined}
    >
      {price(quote)}
      {minute && <span className="ml-1 text-[9px] font-normal text-ink-muted/70">{minute}</span>}
    </td>
  );
}

/** One side's three cells. Extracted because calls and puts differ only
 * in which way they are ITM and which way the numbers read. */
function SideCells({
  quote,
  itm,
  side,
  at,
}: Readonly<{
  quote: SimQuote | null;
  itm: boolean;
  side: "call" | "put";
  at: string;
}>): ReactElement {
  const tint = itm ? "bg-surface-2" : undefined;
  const small = cn("px-2 py-1.5 font-mono text-[11px] tabular-nums text-ink-muted", tint);
  const cells = [
    <PriceCell key="p" quote={quote} at={at} tint={tint} right={side === "call"} />,
    <td key="d" className={cn(small, side === "call" && "text-right")}>
      {delta(quote)}
    </td>,
    <td key="oi" className={cn(small, side === "call" && "text-right")}>
      {openInterest(quote)}
    </td>,
  ];
  // Calls read outward from the strike, puts inward — so the chain is a
  // mirror around the middle column, the way every terminal draws it.
  return <>{side === "call" ? cells : [cells[2], cells[1], cells[0]]}</>;
}

function ChainRowCells({
  row,
  atm,
  onTrade,
  onRemove,
  lots,
  at,
  atmRef,
}: Readonly<RowProps>): ReactElement {
  const isAtm = atm !== null && row.strike === atm;
  return (
    <tr ref={isAtm ? atmRef : undefined} className={cn(isAtm && "bg-accent-bg/60")}>
      <td className="px-2 py-1.5">
        <TradeButtons
          quote={row.call}
          strike={row.strike}
          side="CE"
          onTrade={onTrade}
          onRemove={onRemove}
          lots={lots}
          at={at}
        />
      </td>
      <SideCells quote={row.call} itm={atm !== null && row.strike < atm} side="call" at={at} />
      <td
        className={cn(
          "px-3 py-1.5 text-center font-mono tabular-nums",
          isAtm ? "font-semibold text-accent" : "text-ink-secondary",
        )}
      >
        {row.strike}
      </td>
      <SideCells quote={row.put} itm={atm !== null && row.strike > atm} side="put" at={at} />
      <td className="px-2 py-1.5">
        <TradeButtons
          quote={row.put}
          strike={row.strike}
          side="PE"
          onTrade={onTrade}
          onRemove={onRemove}
          lots={lots}
          at={at}
        />
      </td>
    </tr>
  );
}

interface Props {
  rows: SimChainRow[];
  atm: number | null;
  expiries: number;
  onTrade: (leg: SimLeg) => void;
  onRemove: (strike: number, side: "CE" | "PE") => void;
  lots: Map<string, number>;
  at: string;
}

/** What the archive actually holds for this minute, stated rather than
 * implied.
 *
 * Sessions before 2026-08-08 were captured narrow — a handful of strikes,
 * one expiry, often only one side of each — and later ones hold the full
 * chain. Without this line a thin session renders as a stunted chain and
 * reads as a bug in the page, which is exactly how it was first reported.
 * The counts are facts, not a judgement: no threshold decides when to
 * warn, because "thin" is a spectrum and the reader can see it.
 */
function Coverage({
  rows,
  expiries,
}: Readonly<{ rows: SimChainRow[]; expiries: number }>): ReactElement {
  const legs = rows.reduce((n, r) => n + (r.call ? 1 : 0) + (r.put ? 1 : 0), 0);
  const both = rows.filter((r) => r.call && r.put).length;
  return (
    <p className="text-[11px] text-ink-muted">
      Quoted as of this minute: {rows.length} strikes, {legs} contracts, {expiries}{" "}
      {expiries === 1 ? "expiry" : "expiries"}
      {both < rows.length && ` — ${String(rows.length - both)} strikes hold only one side`}
    </p>
  );
}

/** Keep the ATM row in the middle of the scroll box.
 *
 * Fires ONLY when the ATM itself moves, not on every clock step: re-centring
 * each minute would yank the view back while a reader is scrolled off
 * examining a tail strike. The ATM is stable for long stretches, so this is
 * rare and always in response to the thing it points at having moved.
 */
function useCentreOnAtm(atm: number | null): {
  boxRef: React.RefObject<HTMLDivElement>;
  atmRef: React.RefObject<HTMLTableRowElement>;
} {
  const boxRef = useRef<HTMLDivElement>(null);
  const atmRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    const box = boxRef.current;
    const row = atmRef.current;
    if (!box || !row) return;
    box.scrollTop = row.offsetTop - box.clientHeight / 2 + row.clientHeight / 2;
  }, [atm]);
  return { boxRef, atmRef };
}

function ChainHead(): ReactElement {
  return (
    <thead>
      <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-secondary">
        <th className="px-2 py-2" />
        <th className="px-3 py-2 text-right font-medium">Call LTP</th>
        <th className="px-2 py-2 text-right font-medium">Δ</th>
        <th className="px-2 py-2 text-right font-medium">OI</th>
        <th className="px-3 py-2 text-center font-medium">Strike</th>
        <th className="px-2 py-2 text-left font-medium">OI</th>
        <th className="px-2 py-2 text-left font-medium">Δ</th>
        <th className="px-3 py-2 text-left font-medium">Put LTP</th>
        <th className="px-2 py-2" />
      </tr>
    </thead>
  );
}

export function ChainTable({
  rows,
  atm,
  expiries,
  onTrade,
  onRemove,
  lots,
  at,
}: Readonly<Props>): ReactElement {
  const { boxRef, atmRef } = useCentreOnAtm(atm);

  if (rows.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-secondary">
        No contracts archived for this minute. The session exists — this particular minute has
        nothing in it.
      </p>
    );
  }
  return (
    // h-full + a flex-1 scroll box: the parent sets the height, this owns
    // the scrolling — which must stay HERE, because the ATM centring below
    // drives this element's scrollTop.
    <div className="flex h-full flex-col gap-2">
      <Coverage rows={rows} expiries={expiries} />
      <div ref={boxRef} className="scroll-slim min-h-0 flex-1 overflow-auto">
        <table className="w-full text-sm">
          <ChainHead />
          <tbody className="divide-y divide-line">
            {rows.map((row) => (
              <ChainRowCells
                key={row.strike}
                row={row}
                atm={atm}
                onTrade={onTrade}
                onRemove={onRemove}
                lots={lots}
                at={at}
                atmRef={atmRef}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
